/**
 * Copyright 2025 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Modified by the CHAP-R1 project from the original Google Appoint-Ready source;
 * see the NOTICE file in the repository root.
 */

import React, { useState, useEffect } from "react";
import "./PatientBuilder.css";

const PatientBuilder = ({
  patientName,
  setPatientName,
  onNext,
  isTrainingMode,
  isPatientMode,
  isPrimaryCareMode,
  selectedCase,
  setSelectedCase,
  caseData,
  setCaseData,
}) => {
  const [availableCases, setAvailableCases] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [isLoadingCase, setIsLoadingCase] = useState(false);

  const baseURL = window.location.origin === "http://localhost:3000"
    ? "http://localhost:7860"
    : "";

  // Fetch available cases when in training mode
  useEffect(() => {
    if (isTrainingMode) {
      setIsLoadingCases(true);
      fetch(`${baseURL}/api/list_cases`)
        .then(res => res.json())
        .then(data => {
          setAvailableCases(data.cases || []);
          setIsLoadingCases(false);
        })
        .catch(err => {
          console.error("Error loading cases:", err);
          setIsLoadingCases(false);
        });
    }
  }, [isTrainingMode, baseURL]);

  // Load case data when a case is selected
  useEffect(() => {
    if (selectedCase) {
      setIsLoadingCase(true);
      fetch(`${baseURL}/api/load_case/${selectedCase}`)
        .then(res => res.json())
        .then(data => {
          setCaseData(data.case);
          // Set patient name to case display name
          setPatientName(data.display_name || selectedCase);
          setIsLoadingCase(false);
        })
        .catch(err => {
          console.error("Error loading case:", err);
          setIsLoadingCase(false);
        });
    }
  }, [selectedCase, baseURL, setCaseData, setPatientName]);

  const handleGo = () => {
    if (isTrainingMode) {
      if (selectedCase && caseData) {
        onNext();
      }
    } else {
      if (patientName.trim()) {
        onNext();
      }
    }
  };

  const canProceed = isTrainingMode
    ? (selectedCase && caseData && !isLoadingCase)
    : patientName.trim();

  const getAvatar = () => {
    // In training mode, use generic patient
    if (isTrainingMode) {
      return '/assets/generic_patientTorso.png';
    }

    const name = patientName.trim().toLowerCase();
    const patients = ['alice', 'mike', 'sukumar'];

    const matches = patients.filter(p => name.includes(p));

    if (matches.length === 1) {
      // Capitalize first letter for filename
      const matchedName = matches[0].charAt(0).toUpperCase() + matches[0].slice(1);
      return `/assets/${matchedName}Torso.png`;
    }

    return '/assets/generic_patientTorso.png';
  };

  return (
    <div className="patient-builder-container">
      <div className="frame">
        <div className="selection-section-full">
          <div className="header2">
            {isTrainingMode ? "Training Mode: Standardized Patient" : "CHAP-R1: Automated Patient Intake"}
          </div>
          <div className="patient-info-container">
            <div className="patient-info-form">
              {isTrainingMode ? (
                // Training mode: Case selection dropdown
                <>
                  <label htmlFor="case-select">
                    <strong>Select Standardized Case:</strong>
                  </label>
                  {isLoadingCases ? (
                    <div className="loading-text">Loading cases...</div>
                  ) : (
                    <select
                      id="case-select"
                      className="case-select"
                      value={selectedCase || ""}
                      onChange={(e) => setSelectedCase(e.target.value || null)}
                    >
                      <option value="">-- Select a case --</option>
                      {availableCases.map(c => (
                        <option key={c.case_id} value={c.case_id}>
                          [{c.case_id}] {c.display_name} ({c.domain} - {c.difficulty})
                        </option>
                      ))}
                    </select>
                  )}
                  {caseData && (
                    <div className="case-info">
                      <div className="case-metadata">
                        <strong>Domain:</strong> {caseData.metadata?.domain || "N/A"}<br />
                        <strong>Difficulty:</strong> {caseData.metadata?.intended_difficulty || "N/A"}<br />
                        <strong>Core Threat:</strong> {caseData.metadata?.core_threat || "N/A"}
                      </div>
                    </div>
                  )}
                  <div className="lighttext instruction-text">
                    In Training Mode, a Standardized Patient (powered by AI) will respond to the interviewer's questions based on the selected case scenario.
                    <br /><br />
                    After the interview, you can grade the interaction against the case rubric.
                  </div>
                </>
              ) : (
                // Normal mode: Name input
                <>
                  <label htmlFor="patient-name">
                    <strong>Your Name:</strong>
                  </label>
                  <input
                    id="patient-name"
                    type="text"
                    className="name-input"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    placeholder="type your name here"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && patientName.trim()) {
                        handleGo();
                      }
                    }}
                  />
                  <div className="lighttext instruction-text">
                    <p>In order to get the most out of your visit, we are piloting the use of AI to take a medical history.</p>
                    <ul className="intake-bullets" style={{ textAlign: 'left', margin: '10px 0', paddingLeft: '20px' }}>
                      <li><strong>Share your symptoms:</strong> The AI will ask you about what you are experiencing.</li>
                      <li><strong>Be detailed:</strong> Answer with as much detail as you think is needed.</li>
                      <li><strong>Doctor review:</strong> Your doctor will review this information with you to make decisions on tests and treatments.</li>
                      <li><strong>Clarify mistakes:</strong> You can clarify any issues with the AI or your care team.</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            <div className="avatar-display">
              <img
                src={getAvatar()}
                alt="Patient Avatar"
                className="patient-torso-image"
              />
            </div>
          </div>
        </div>
        <button
          className="info-button"
          onClick={handleGo}
          disabled={!canProceed}
        >
          {isTrainingMode ? "Start Training Interview" : "Start Conversation"}
        </button>
        {isTrainingMode && (
          <div className="disclaimer-footnote" style={{ fontSize: '0.8em', color: '#666', marginTop: '10px', textAlign: 'center' }}>
            Training Mode: The standardized patient will be simulated by AI. No real patient data is used.
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientBuilder;

